import React from "react";
import { GetStaticProps } from "next";
import StaticContainer from "../layouts/StaticContainer";
import styles from "../styles/pages/partners.module.scss";
import Grid from "@mui/material/Grid";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GeneralForm from "../components/_shared/GeneralForm";
import { hubspot } from "../constants";
import { Trans } from "next-i18next";
import { useBrandContact, useLiveTranslation } from "../lib/LiveContext";
import { cmsPageProps } from "../lib/pageProps";
import Meta from "../layouts/Meta";
import Link from "next/link";
import Image from "next/image";
import { PartnerTile, getCmsPartners } from "../services/cms/partners";

interface Props {
  tileDataCurrent: PartnerTile[];
  tileDataPrevious: PartnerTile[];
}

const Partners = ({ tileDataCurrent, tileDataPrevious }: Props) => {
  const { t } = useLiveTranslation("partners");
  const contact = useBrandContact();

  function TabPanel(props) {
    const { children, value, index } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
      >
        {value === index && (
          <Box>
            <div>{children}</div>
          </Box>
        )}
      </div>
    );
  }

  TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
  };

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  const [value, setValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const [expanded, setExpanded] = React.useState<string | false>("");

  const handleChangeAccordion =
    (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  return (
    <StaticContainer title={t("title")} category="About">
      <Meta
        title={t("title")}
        description={t("meta-description")}
        keywords={t("meta-keyword")}
      />

      <section
        className="fullWidthBG"
        style={{
          backgroundImage: `url(
            "https://cdn.solssmart.org/static/partners/donate-to-sols-foundation-header.jpg"
          )`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          marginTop: "-35px",
        }}
      >
        <Grid container alignItems="center" sx={{ padding: "15px 35px" }}>
          <Grid item sm={12} md={5}>
            <ul className={styles.statsList}>
              <li>
                <span>25,136 {t("stat-lives")}</span> {t("stat-improved")}
              </li>
              <li>
                <span>2,266 {t("stat-classes")}</span> {t("stat-conducted")}
              </li>
              <li>
                <span>2,626 {t("stat-hours")}</span> {t("stat-served")}
              </li>
            </ul>
          </Grid>
          <Grid item sm={12} md={2} lg={3}></Grid>
          <Grid item sm={12} md={5} lg={4}>
            <div className={styles.rightSection}>
              <h2>{t("award-winning")}</h2>
              <p>{`(SSM No. ${contact.registrationNumberOld})`}</p>
            </div>
          </Grid>
        </Grid>
      </section>

      <section>
        <Grid container spacing={7} pt={5} pb={3}>
          <Grid item xs={12} sm={6}>
            <h2>{t("partner-family-h2")}</h2>
            <p>
              <strong>{t("partner-family-p1")}</strong>
            </p>
            <p>{t("partner-family-p2")}</p>
            <p>{t("partner-family-p3")}</p>
            <p>{t("partner-family-p4")}</p>
            <p>
              <strong>
                <Link href="https://donate.sols.foundation">
                  {t("partner-family-p5")}
                </Link>{" "}
                {t("partner-family-p5b")}
              </strong>
            </p>
          </Grid>

          <Grid item xs={12} sm={6}>
            <div style={{ width: "100%" }}>
              <Box className={styles.partnerTabBox}>
                <Tabs
                  value={value}
                  onChange={handleChange}
                  centered
                  aria-label="basic tabs example"
                  indicatorColor="primary"
                  className={styles.partnerTabs}
                >
                  <Tab label={t("current-partners-span")} {...a11yProps(0)} />
                  <Tab label={t("previous-partners-span")} {...a11yProps(1)} />
                </Tabs>
              </Box>
              <TabPanel value={value} index={0}>
                <div className={styles.imgGalleryCurrent}>
                  <ImageList
                    rowHeight={"auto"}
                    className={styles.gridList}
                    cols={3}
                  >
                    {tileDataCurrent?.map((tile) => (
                      <ImageListItem className={styles.gridImageTile} key={tile.img} cols={1}>
                        <img src={tile.img} alt={tile.title} className={styles.gridImage} />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </div>
              </TabPanel>
              <TabPanel value={value} index={1}>
                <div className={styles.imgGalleryPrevious}>
                  <ImageList
                    rowHeight={"auto"}
                    className={styles.gridList}
                    cols={4}
                  >
                    {tileDataPrevious?.map((tile) => (
                      <ImageListItem className={styles.gridImageTile} key={tile.img} cols={1}>
                        <img src={tile.img} alt={tile.title} className={styles.gridImage} />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </div>
              </TabPanel>
            </div>
          </Grid>
        </Grid>
      </section>

      <section className="fullWidthBG last">
        <div className="fullWidthInner">
          <div className="centerContent">
            <h2>{t("become-partner-h2")}</h2>
            <p>{t("become-partner-p1")}</p>
            <p>{t("become-partner-p2")}</p>
            <p>
              <strong>{t("become-partner-p3")}</strong>
            </p>
          </div>
          <Grid container spacing={10} alignItems="baseline">
            <Grid item xs={12} sm={7}>
              <h2>{t("partner-benefits-h2")}</h2>
              <p>{t("partner-benefits-p1")}</p>
              <ol>
                <li>{t("partner-benefits-li1")}</li>
                <li>{t("partner-benefits-li2")}</li>
                <li>{t("partner-benefits-li3")}</li>
                <li>{t("partner-benefits-li4")}</li>
                <li>{t("partner-benefits-li5")}</li>
                <li>{t("partner-benefits-li6")}</li>
                <li>{t("partner-benefits-li7")}</li>
                <li>{t("partner-benefits-li8")}</li>
                <li>{t("partner-benefits-li9")}</li>
              </ol>
              <p>
                <em>
                  <Trans
                    i18nKey="partner-benefits-p2"
                    t={t}
                    components={{ strong: <strong /> }}
                  />
                </em>
              </p>

              <h3 style={{ paddingTop: "40px" }}>{t("faq-h3")}</h3>
              <Accordion
                expanded={expanded === "panel1"}
                onChange={handleChangeAccordion("panel1")}
                elevation={0}
                className={styles.accordionItem}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon className={styles.accordionIcon} />
                  }
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                  className={styles.accordionHeading}
                >
                  <h4>{t("why-partner-q1")}</h4>
                </AccordionSummary>
                <AccordionDetails className={styles.accordionDetails}>
                  <p>
                    {t("why-partner-a1")}
                    <br />
                    <br />
                    {t("why-partner-a1ii")}
                  </p>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expanded === "panel2"}
                onChange={handleChangeAccordion("panel2")}
                elevation={0}
                className={styles.accordionItem}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon className={styles.accordionIcon} />
                  }
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                  className={styles.accordionHeading}
                >
                  <h4>{t("why-partner-q2")}</h4>
                </AccordionSummary>
                <AccordionDetails className={styles.accordionDetails}>
                  <p>{t("why-partner-a2")}</p>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expanded === "panel3"}
                onChange={handleChangeAccordion("panel3")}
                elevation={0}
                className={styles.accordionItem}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon className={styles.accordionIcon} />
                  }
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                  className={styles.accordionHeading}
                >
                  <h4>{t("why-partner-q3")}</h4>
                </AccordionSummary>
                <AccordionDetails className={styles.accordionDetails}>
                  <p>
                    {t("why-partner-a3")}
                    <Link href="/impact">{t("why-partner-a3ii")}</Link>
                  </p>
                </AccordionDetails>
              </Accordion>
            </Grid>

            <Grid item xs={12} sm={5}>
              <GeneralForm
                title={t("contact-h3")}
                description={t("contact-p")}
                portalId={hubspot.PORTAL_ID}
                formId={hubspot.PARTNERS_ID}
              />
            </Grid>
          </Grid>
        </div>
      </section>
    </StaticContainer>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const { current, previous } = await getCmsPartners();

  return {
    props: {
      tileDataCurrent: current,
      tileDataPrevious: previous,
      ...(await cmsPageProps(locale, ["partners"])),
    },
    revalidate: 60,
  };
};

export default Partners;
